import { Component, OnInit, OnDestroy, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { trigger, style, transition, animate } from '@angular/animations';
import { LandingService } from '../../services/Landing_Services/landing.service';
import { Slider } from '../../utilities/interfaces/Document_Interfaces/slider_interfaces/slider.interface';

interface HeroTextStyle {
  color?: string;
  'background-color'?: string;
  background?: string;
}

interface HeroTextSegment {
  text: string;
  style: HeroTextStyle;
}

interface HeroTextBlock {
  text: string;
  style: HeroTextStyle;
  segments: HeroTextSegment[];
}

interface HeroSlider extends Slider {
  heroTitle: string;
  heroSubtitle: string;
  heroTitleSegments: HeroTextSegment[];
  heroSubtitleSegments: HeroTextSegment[];
}

@Component({
  selector: 'app-hero',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './hero.component.html',
  styleUrl: './hero.component.css',
  animations: [
    trigger('slideFade', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('800ms ease-in', style({ opacity: 1 }))
      ]),
      transition('* => *', [
        style({ opacity: 0 }),
        animate('800ms ease-in', style({ opacity: 1 }))
      ])
    ]),
  ]
})
export class HeroComponent implements OnInit, OnDestroy {
  private readonly sliderDelayMs = 5000;
  sliders = signal<HeroSlider[]>([]);
  currentSliderIndex = signal<number>(0);
  currentSlider = computed(() => this.sliders()[this.currentSliderIndex()] ?? null);
  private intervalId?: ReturnType<typeof setInterval>;

  constructor(private landingService: LandingService) {}

  ngOnInit(): void {
    this.loadSliders();
  }

  ngOnDestroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  loadSliders(): void {
    this.landingService.getSliders().subscribe({
      next: (data) => {
        const publishedSliders = data
          .filter((s) => s.published === 'Yes')
          .map((s) => ({
            ...s,
            ...this.getDescriptionParts(s.description || ''),
          }));
        this.sliders.set(publishedSliders);

        this.currentSliderIndex.set(0);
        if (this.intervalId) {
          clearInterval(this.intervalId);
        }

        if (publishedSliders.length > 1) {
          this.intervalId = setInterval(() => {
            this.currentSliderIndex.update((idx) => (idx + 1) % this.sliders().length);
          }, this.sliderDelayMs);
        }
      },
      error: (error) => {
        console.error('Error loading sliders:', error);
      },
    });
  }

  private getDescriptionParts(
    description: string,
  ): Pick<HeroSlider, 'heroTitle' | 'heroSubtitle' | 'heroTitleSegments' | 'heroSubtitleSegments'> {
    const textBlocks = this.extractTextBlocks(description);
    const [titleBlock = { text: '', style: {}, segments: [] }, ...subtitleBlocks] = textBlocks;
    const heroSubtitle = subtitleBlocks.map((block) => block.text).join(' ');

    return {
      heroTitle: titleBlock.text,
      heroSubtitle,
      heroTitleSegments: titleBlock.segments.length > 0
        ? titleBlock.segments
        : [{ text: titleBlock.text, style: titleBlock.style }],
      heroSubtitleSegments: this.joinSegmentsWithSpaces(subtitleBlocks),
    };
  }

  private extractTextBlocks(html: string): HeroTextBlock[] {
    if (!html.trim()) {
      return [];
    }

    if (typeof document === 'undefined') {
      const text = html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
      return text ? [{ text, style: {}, segments: [{ text, style: {} }] }] : [];
    }

    const container = document.createElement('div');
    container.innerHTML = html;
    const blocks: HeroTextBlock[] = [];

    Array.from(container.childNodes).forEach((node) => {
      const text = node.textContent?.replace(/\s+/g, ' ').trim() || '';
      if (!text) {
        return;
      }

      const segments = this.extractTextSegments(node, {});
      blocks.push({
        text,
        style: this.mergeTextStyles(segments.map((segment) => segment.style)),
        segments: this.trimSegments(segments),
      });
    });

    return blocks;
  }

  private extractTextSegments(node: Node, inheritedStyle: HeroTextStyle): HeroTextSegment[] {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent?.replace(/\s+/g, ' ') || '';
      return text.trim() ? [{ text, style: inheritedStyle }] : [];
    }

    if (!(node instanceof HTMLElement)) {
      return [];
    }

    const style = { ...inheritedStyle, ...this.getOwnColorStyle(node) };
    return Array.from(node.childNodes).flatMap((childNode) =>
      this.extractTextSegments(childNode, style),
    );
  }

  private getOwnColorStyle(element: HTMLElement): HeroTextStyle {
    const style: HeroTextStyle = {};
    const color = element.style.color || element.getAttribute('color') || '';
    const backgroundColor = element.style.backgroundColor || element.getAttribute('bgcolor') || '';
    const background = element.style.background || '';

    if (color) {
      style.color = color;
    }
    if (backgroundColor) {
      style['background-color'] = backgroundColor;
    }
    if (background) {
      style.background = background;
    }

    return style;
  }

  private joinSegmentsWithSpaces(blocks: HeroTextBlock[]): HeroTextSegment[] {
    return blocks.flatMap((block, index) =>
      index === 0 ? block.segments : [{ text: ' ', style: {} }, ...block.segments],
    );
  }

  private trimSegments(segments: HeroTextSegment[]): HeroTextSegment[] {
    if (segments.length === 0) {
      return [];
    }

    return segments
      .map((segment, index) => {
        let text = segment.text;
        if (index === 0) {
          text = text.trimStart();
        }
        if (index === segments.length - 1) {
          text = text.trimEnd();
        }
        return { ...segment, text };
      })
      .filter((segment) => segment.text.trim());
  }

  private mergeTextStyles(styles: HeroTextStyle[]): HeroTextStyle {
    return styles.reduce<HeroTextStyle>((merged, style) => ({ ...merged, ...style }), {});
  }
}
